<script setup lang="ts">
import { computed } from 'vue'
import {
  ECOMMERCE_ITEM_BENEFIT_TYPE,
  type EcommerceItem,
  type EcommerceItemBenefit,
} from '@/types/shared/player-config'
import { formatNumberFullTruncate } from '@/helpers/utils'
import { usePlayer } from '@/composables/game/player'
import { allSpecialCurrenciesConfig } from '@/config/special-currencies'
import { benefitConfigMap, benefitOrder, benefitTypeToKey } from '@/config/benefit-display'

const props = defineProps<{
	limitedPack: EcommerceItem
}>()

const { goldenCoinValue } = usePlayer()

interface BenefitDisplayItem {
	image: string
	alt: string
	value: string | number
}

const getGoldenCoinBenefitAmount = (benefit: EcommerceItemBenefit) =>
  benefit.amount * goldenCoinValue.value

const formatDurationValue = (amount: number) =>
  amount < 1 ? Math.floor(amount * 60) : Math.floor(amount)

const displayedBenefits = computed(() => {
  const result: Array<{ benefit: EcommerceItemBenefit; type: string }> = []

  for (const benefitType of benefitOrder) {
    if (benefitType === ECOMMERCE_ITEM_BENEFIT_TYPE.SPECIAL_CURRENCY) {
      props.limitedPack.benefits
        .filter(b => b.type === benefitType)
        .forEach(benefit => result.push({ benefit, type: 'specialCurrency' }))
    } else {
      const benefit = props.limitedPack.benefits.find(b => b.type === benefitType)
      const type = benefitTypeToKey[benefitType]
      if (benefit && type) {
        result.push({ benefit, type })
      }
    }
  }

  return result
})

const benefitDisplayItems = computed<BenefitDisplayItem[]>(() => {
  return displayedBenefits.value.map((item) => {
    if (item.type === 'specialCurrency' && item.benefit.type === ECOMMERCE_ITEM_BENEFIT_TYPE.SPECIAL_CURRENCY) {
      const currencyConfig = allSpecialCurrenciesConfig.value[item.benefit.currency]
      return {
        image: currencyConfig?.image || '',
        alt: currencyConfig?.label || item.benefit.currency,
        value: formatNumberFullTruncate(item.benefit.amount, false, false, true),
      }
    }

    const config = benefitConfigMap[item.type]
    if (!config) return null

    let value: string | number

    if (item.type === 'coin') {
      value = formatNumberFullTruncate(getGoldenCoinBenefitAmount(item.benefit), false, false, true)
    } else if (item.type === 'tphx2' || item.type === 'offlineProfit') {
      value = formatDurationValue(item.benefit.amount)
    } else {
      value = formatNumberFullTruncate(item.benefit.amount, false, false, true)
    }

    return {
      ...config,
      value,
    }
  }).filter((item): item is BenefitDisplayItem => item !== null)
})

const gapClass = computed(() => {
  const count = benefitDisplayItems.value.length
  if (count <= 3) return 'gap-[13px]'
  return 'gap-[10px]'
})

const shouldWrap = computed(() => benefitDisplayItems.value.length > 4)

const imageWrapperHeight = computed(() => {
  const count = benefitDisplayItems.value.length
  if (count <= 3) return 'h-[59px]'
  return 'h-[43px]'
})

const imageWrapperSize = computed(() => {
  const count = benefitDisplayItems.value.length
  if (count <= 3) return 'h-[59px] w-[59px]'
  return 'h-[43px] w-[43px]'
})

const textSize = computed(() => {
  const count = benefitDisplayItems.value.length
  if (count <= 3) return 'text-[37px] leading-[44px]'
  return 'text-[27px] leading-8'
})

const valueMarginTop = computed(() => {
  const count = benefitDisplayItems.value.length
  if (count <= 3) return '-mt-[18px]'
  return '-mt-[13px]'
})
</script>

<template>
  <div
    :class="['mt-9 w-full flex items-start justify-center relative', gapClass, { 'flex-wrap': shouldWrap }]"
  >
    <div
      class="absolute inset-0 rounded-[297px] opacity-[0.66] bg-[radial-gradient(50%_50%_at_50%_50%,#FFF_33.65%,rgba(255,255,255,0)_100%)] blur-[26px]"
    />
    <template
      v-for="(item, index) in benefitDisplayItems"
      :key="index"
    >
      <div
        v-if="index > 0"
        :class="['flex items-center justify-center', imageWrapperHeight]"
      >
        <span
          :class="['benefit-value font-bold', textSize]"
        >
          +
        </span>
      </div>
      <div class="flex flex-col items-center">
        <div :class="['flex items-center justify-center z-[1]', imageWrapperSize]">
          <img
            :src="item.image"
            class="h-full w-auto drop-shadow-[0_0_6px_rgba(0,0,0,0.44)]"
            :alt="item.alt"
          >
        </div>
        <span
          :class="['benefit-value font-bold z-[2] uppercase text-center', textSize, valueMarginTop]"
        >
          {{ item.value }}
        </span>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
.benefit-value {
	color: #1100FF;
	-webkit-text-stroke-width: 5px;
	-webkit-text-stroke-color: #36EEFF;
	paint-order: stroke fill;
	filter: drop-shadow(0 0 24px #10F);
	@apply px-1;
}
</style>